import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import api from '../../utils/api';
import { gettyApi } from '../../utils/gettyApi';
import { en } from '../../translations/en';

interface HierarchyStatus {
  exists: boolean;
  count: number | null;
  createdAt: string | null;
  isFresh: boolean;
  conceptId?: string;
  conceptName?: string;
  hasCustomQuery?: boolean;
}

interface GettyStatus {
  hierarchies: {
    workTypes: HierarchyStatus;
    materials: HierarchyStatus;
    techniques: HierarchyStatus;
  };
  signatures: {
    exists: boolean;
    count: number;
  };
  timestamp: string;
}

interface GettyConfig {
  [key: string]: {
    conceptId: string;
    conceptName: string;
    sparqlTemplate?: string;
  };
}

interface PreviewResult {
  conceptId: string;
  conceptLabel: string;
  totalResults: number;
  topLevelNodes: { id: string; label: string; childCount: number }[];
  topLevelNodeCount: number;
  treeNodeCount: number;
  treeDepth: number;
  valid: boolean;
  error?: string;
  validationWarnings?: string[];
  queryUsed?: string;
}

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString();
};

const GettyTab: React.FC = () => {
  const [gettyStatus, setGettyStatus] = useState<GettyStatus | null>(null);
  const [gettyConfig, setGettyConfig] = useState<GettyConfig | null>(null);
  const [gettyLoading, setGettyLoading] = useState(false);
  const [gettyError, setGettyError] = useState<string | null>(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildSuccess, setRebuildSuccess] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [confirmRefreshOpen, setConfirmRefreshOpen] = useState(false);

  // Edit/preview state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [currentConceptId, setCurrentConceptId] = useState('');
  const [newConceptId, setNewConceptId] = useState('');
  const [queryTemplate, setQueryTemplate] = useState('');
  const [originalQueryTemplate, setOriginalQueryTemplate] = useState('');
  const [queryModified, setQueryModified] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);

  const fetchGettyStatus = useCallback(async () => {
    setGettyLoading(true);
    setGettyError(null);
    try {
      const [statusRes, configRes] = await Promise.all([
        api.get('/api/admin/getty/status'),
        api.get('/api/admin/getty/config'),
      ]);
      setGettyStatus(statusRes.data);
      setGettyConfig(configRes.data);
    } catch (_err: unknown) {
      setGettyError(en.admin.getty.rebuildError);
    } finally {
      setGettyLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGettyStatus();
  }, [fetchGettyStatus]);

  const handleRebuildClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmRebuild = async () => {
    setConfirmDialogOpen(false);
    setRebuilding(true);
    setRebuildSuccess(null);
    setGettyError(null);

    try {
      await api.post('/api/admin/getty/rebuild?conceptType=all&force=true');
      setRebuildSuccess(en.admin.getty.rebuildSuccess);
      gettyApi.clearCache();
      fetchGettyStatus();
    } catch (_err: unknown) {
      setGettyError(en.admin.getty.rebuildError);
    } finally {
      setRebuilding(false);
    }
  };

  const handleRefreshClick = () => {
    setConfirmRefreshOpen(true);
  };

  const handleConfirmRefresh = async () => {
    setConfirmRefreshOpen(false);
    setRefreshing(true);
    setRebuildSuccess(null);
    setGettyError(null);

    try {
      const response = await api.post('/api/admin/getty/refresh');
      const message = response.data?.fetch?.status === 'unchanged'
        ? en.admin.getty.refreshUnchanged
        : en.admin.getty.refreshSuccess;
      setRebuildSuccess(message);
      gettyApi.clearCache();
      fetchGettyStatus();
    } catch (_err: unknown) {
      setGettyError(en.admin.getty.refreshError);
    } finally {
      setRefreshing(false);
    }
  };

  const handleEditClick = (hierarchyType: string, label: string, conceptId: string) => {
    setEditingType(hierarchyType);
    setEditingLabel(label);
    setCurrentConceptId(conceptId);
    setNewConceptId(conceptId);
    setPreviewResult(null);
    setPreviewError(null);

    // Load query template from config
    const template = gettyConfig?.[hierarchyType]?.sparqlTemplate ?? '';
    setQueryTemplate(template);
    setOriginalQueryTemplate(template);
    setQueryModified(false);

    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingType(null);
    setPreviewResult(null);
    setPreviewError(null);
  };

  const handleQueryChange = (value: string) => {
    setQueryTemplate(value);
    setQueryModified(value !== originalQueryTemplate);
    setPreviewResult(null);
    setPreviewError(null);
  };

  const handleResetQuery = () => {
    setQueryTemplate(originalQueryTemplate);
    setQueryModified(false);
    setPreviewResult(null);
    setPreviewError(null);
  };

  const handlePreview = async () => {
    if (!/^\d{6,12}$/.test(newConceptId)) {
      setPreviewError(en.admin.getty.invalidConceptId);
      return;
    }

    setPreviewing(true);
    setPreviewResult(null);
    setPreviewError(null);

    try {
      const body: { conceptId: string; sparqlTemplate?: string } = {
        conceptId: newConceptId,
      };
      if (queryModified) {
        body.sparqlTemplate = queryTemplate;
      }
      const response = await api.post('/api/admin/getty/preview', body);
      setPreviewResult(response.data);
    } catch (_err: unknown) {
      setPreviewError(en.admin.getty.previewError);
    } finally {
      setPreviewing(false);
    }
  };

  const handleApply = async () => {
    if (!editingType || !previewResult?.valid) return;

    setApplying(true);
    setGettyError(null);

    try {
      const body: {
        hierarchyType: string;
        conceptId: string;
        conceptName: string;
        sparqlTemplate?: string;
      } = {
        hierarchyType: editingType,
        conceptId: newConceptId,
        conceptName: previewResult.conceptLabel,
      };
      if (queryModified) {
        body.sparqlTemplate = queryTemplate;
      }
      await api.put('/api/admin/getty/config', body);
      setRebuildSuccess(en.admin.getty.applySuccess);
      gettyApi.clearCache();
      handleEditClose();
      fetchGettyStatus();
    } catch (_err: unknown) {
      setGettyError(en.admin.getty.applyError);
    } finally {
      setApplying(false);
    }
  };

  const renderHierarchyCard = (
    label: string,
    hierarchyType: string,
    hierarchy: HierarchyStatus,
  ) => (
    <Grid item xs={12} md={6}>
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {label}
              </Typography>
              {hierarchy.hasCustomQuery && (
                <Chip
                  label={en.admin.getty.customQuery}
                  size="small"
                  variant="outlined"
                  color="info"
                />
              )}
            </Box>
            {hierarchy.conceptId && (
              <Tooltip title={en.admin.getty.editConceptId}>
                <IconButton
                  size="small"
                  onClick={() => handleEditClick(hierarchyType, label, hierarchy.conceptId ?? '')}
                  disabled={rebuilding}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {hierarchy.conceptId && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {en.admin.getty.conceptId}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {hierarchy.conceptId}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {en.admin.getty.count}
            </Typography>
            <Typography variant="body2">
              {hierarchy.count?.toLocaleString() ?? '-'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {en.admin.getty.lastUpdated}
            </Typography>
            <Typography variant="body2">
              {formatDate(hierarchy.createdAt)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Chip
              label={
                !hierarchy.exists
                  ? en.admin.getty.notLoaded
                  : hierarchy.isFresh
                    ? en.admin.getty.fresh
                    : en.admin.getty.stale
              }
              size="small"
              color={
                !hierarchy.exists
                  ? 'error'
                  : hierarchy.isFresh
                    ? 'success'
                    : 'warning'
              }
            />
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">{en.admin.getty.title}</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleRefreshClick}
              disabled={refreshing || rebuilding || gettyLoading}
              startIcon={refreshing ? <CircularProgress size={20} color="inherit" /> : undefined}
            >
              {refreshing ? en.admin.getty.refreshing : en.admin.getty.refreshDump}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRebuildClick}
              disabled={rebuilding || refreshing || gettyLoading}
              startIcon={rebuilding ? <CircularProgress size={20} color="inherit" /> : undefined}
            >
              {rebuilding ? en.admin.getty.rebuilding : en.admin.getty.rebuildAll}
            </Button>
          </Box>
        </Box>

        {rebuildSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRebuildSuccess(null)}>
            {rebuildSuccess}
          </Alert>
        )}

        {gettyError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGettyError(null)}>
            {gettyError}
          </Alert>
        )}

        {rebuilding && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress data-testid="rebuild-progress" />
          </Box>
        )}

        {gettyLoading && !rebuilding && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {gettyStatus && !gettyLoading && !rebuilding && (
          <Grid container spacing={3}>
            {renderHierarchyCard(en.admin.getty.workTypes, 'workTypes', gettyStatus.hierarchies.workTypes)}
            {renderHierarchyCard(en.admin.getty.materials, 'materials', gettyStatus.hierarchies.materials)}
            {renderHierarchyCard(en.admin.getty.techniques, 'techniques', gettyStatus.hierarchies.techniques)}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {en.admin.getty.signatures}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {en.admin.getty.count}
                    </Typography>
                    <Typography variant="body2">
                      {gettyStatus.signatures.count?.toLocaleString() ?? '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Chip
                      label={
                        gettyStatus.signatures.exists
                          ? en.admin.getty.fresh
                          : en.admin.getty.notLoaded
                      }
                      size="small"
                      color={gettyStatus.signatures.exists ? 'success' : 'error'}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Rebuild confirmation dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>{en.admin.getty.rebuildAll}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {en.admin.getty.confirmRebuild}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmRebuild} variant="contained" color="primary">
            {en.admin.getty.rebuild}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refresh-from-upstream confirmation dialog */}
      <Dialog
        open={confirmRefreshOpen}
        onClose={() => setConfirmRefreshOpen(false)}
      >
        <DialogTitle>{en.admin.getty.refreshDump}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {en.admin.getty.confirmRefresh}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRefreshOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmRefresh} variant="contained" color="primary">
            {en.admin.getty.refreshDump}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit concept ID + SPARQL query dialog with preview */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {en.admin.getty.editConceptId}: {editingLabel}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {en.admin.getty.currentConfig}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {currentConceptId}
            </Typography>
          </Box>

          <TextField
            label={en.admin.getty.newConceptId}
            value={newConceptId}
            onChange={(e) => {
              setNewConceptId(e.target.value);
              setPreviewResult(null);
              setPreviewError(null);
            }}
            fullWidth
            size="small"
            sx={{ mb: 3 }}
            placeholder="e.g. 300191086"
            disabled={previewing || applying}
          />

          {/* SPARQL Query Template Editor */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">
                {en.admin.getty.sparqlTemplate}
              </Typography>
              {queryModified && (
                <Button
                  size="small"
                  variant="text"
                  onClick={handleResetQuery}
                  disabled={previewing || applying}
                >
                  {en.admin.getty.resetToDefault}
                </Button>
              )}
            </Box>
            <TextField
              value={queryTemplate}
              onChange={(e) => handleQueryChange(e.target.value)}
              fullWidth
              multiline
              minRows={8}
              maxRows={16}
              disabled={previewing || applying}
              helperText={en.admin.getty.sparqlHelperText}
              slotProps={{
                input: {
                  sx: { fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 1.5 },
                },
              }}
            />
          </Box>

          <Button
            variant="outlined"
            onClick={handlePreview}
            disabled={previewing || applying || !newConceptId}
            startIcon={previewing ? <CircularProgress size={16} /> : undefined}
            sx={{ mb: 2 }}
          >
            {previewing ? en.admin.getty.previewing : en.admin.getty.preview}
          </Button>

          {previewError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {previewError}
            </Alert>
          )}

          {previewResult && !previewResult.valid && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {previewResult.error}
            </Alert>
          )}

          {previewResult?.validationWarnings && previewResult.validationWarnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {previewResult.validationWarnings.join('; ')}
            </Alert>
          )}

          {previewResult?.valid && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {en.admin.getty.previewResults}
                {previewResult.queryUsed === 'custom' && (
                  <Chip label={en.admin.getty.customQuery} size="small" variant="outlined" color="info" sx={{ ml: 1 }} />
                )}
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {en.admin.getty.conceptLabel}
                </Typography>
                <Typography variant="body2">{previewResult.conceptLabel}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {en.admin.getty.totalResults}
                </Typography>
                <Typography variant="body2">{previewResult.totalResults.toLocaleString()}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {en.admin.getty.topLevelNodes}
                </Typography>
                <Typography variant="body2">{previewResult.topLevelNodeCount}</Typography>
              </Box>

              <Divider sx={{ mb: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Sample top-level nodes ({Math.min(previewResult.topLevelNodes.length, 20)} of {previewResult.topLevelNodeCount})
              </Typography>
              <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                {previewResult.topLevelNodes.slice(0, 20).map((node) => (
                  <ListItem key={node.id} sx={{ py: 0 }}>
                    <ListItemText
                      primary={node.label}
                      secondary={node.childCount > 0 ? `${node.childCount} children` : undefined}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} disabled={applying}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            color="primary"
            disabled={!previewResult?.valid || applying}
            startIcon={applying ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {applying ? en.admin.getty.applying : en.admin.getty.applyAndRebuild}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GettyTab;
